import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { featured } from "../data/featuredProjects";
import styles from "./ProjectDetails.module.css";
import CustomButton from "../components/CustomButton";
import BackToTop from "../components/BackToTop";
import ScrollStack from "../components/ScrollStack";
import NotFound from "./NotFound";

const isNonEmpty = (v: any) =>
  (typeof v === "string" && v.trim().length > 0) ||
  (Array.isArray(v) && v.length > 0);

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const project = featured.find((p) => p.id === Number(id));
  const [showDescription, setShowDescription] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  if (!project) {
    return (
      <main className={styles.projectDetails}>
        <NotFound />
      </main>
    );
  }


  const {
    title,
    location,
    description,
    builder,
    leistungen,
    projektzeitraum,
    architekt,
    nutzung,
    bruttoGrundflaeche,
    wohnflaeche,
    gewerbeflaeche,
    grundstuecksflaeche,
    nutzflaeche,
    wohnungsschluessel,
    anzahlZimmer,
    anzahlStellplaetze,
    besonderheit,
    gewerbeeinheiten,
  } = project as any;


  const paragraphs = isNonEmpty(description)
    ? String(description)
        .replace(/\r/g, "")
        .split(/\n{2,}/)
        .map((p) => p.replace(/\n+/g, " ").trim())
        .filter(Boolean)
    : [];


  const descriptionMarkup = (
    <div ref={descriptionRef}>
      <section className={styles.descriptionSection}>
        <div className={styles.descriptionInner}>
          <h1 data-desc-title id="project-desc-title">
            {title}
          </h1>
          {isNonEmpty(location) && <h3>{location}</h3>}

          {paragraphs.length > 0 && (
            <div className={styles.projectDescriptionWrapper}>
              <div className={styles.projectDescriptionText}>
                {paragraphs.map((paragraph: string, idx: number) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          <ul className={styles.cardList}>
            {isNonEmpty(builder) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Bauherr / Auftraggeber</div>
                <div className={styles.cardValue}>{builder}</div>
              </li>
            )}

            {isNonEmpty(leistungen) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Leistungen</div>
                <div className={styles.cardValue}>{leistungen}</div>
              </li>
            )}

            {isNonEmpty(projektzeitraum) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Projektzeitraum</div>
                <div className={styles.cardValue}>{projektzeitraum}</div>
              </li>
            )}

            {architekt && typeof architekt === "object" ? (
              <>
                {isNonEmpty(architekt.hochbau) && (
                  <li className={styles.cardItem}>
                    <div className={styles.cardLabel}>
                      Architektur (Hochbau)
                    </div>
                    <div className={styles.cardValue}>{architekt.hochbau}</div>
                  </li>
                )}
                {isNonEmpty(architekt.innenausstattung) && (
                  <li className={styles.cardItem}>
                    <div className={styles.cardLabel}>Innenausstattung</div>
                    <div className={styles.cardValue}>
                      {architekt.innenausstattung}
                    </div>
                  </li>
                )}
              </>
            ) : (
              isNonEmpty(architekt) && (
                <li className={styles.cardItem}>
                  <div className={styles.cardLabel}>Architektur</div>
                  <div className={styles.cardValue}>{architekt}</div>
                </li>
              )
            )}

            {isNonEmpty(nutzung) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Nutzung</div>
                <div className={styles.cardValue}>{nutzung}</div>
              </li>
            )}

            {isNonEmpty(wohnflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohnfläche</div>
                <div className={styles.cardValue}>{wohnflaeche}</div>
              </li>
            )}

            {isNonEmpty(gewerbeflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Gewerbefläche</div>
                <div className={styles.cardValue}>{gewerbeflaeche}</div>
              </li>
            )}

            {isNonEmpty(nutzflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Nutzfläche</div>
                <div className={styles.cardValue}>{nutzflaeche}</div>
              </li>
            )}

            {isNonEmpty(bruttoGrundflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Bruttogrundfläche (BGF)</div>
                <div className={styles.cardValue}>{bruttoGrundflaeche}</div>
              </li>
            )}

            {isNonEmpty(grundstuecksflaeche) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Grundstücksgröße</div>
                <div className={styles.cardValue}>{grundstuecksflaeche}</div>
              </li>
            )}

            {isNonEmpty(wohnungsschluessel) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohnungsschlüssel</div>
                <div className={styles.cardValue}>{wohnungsschluessel}</div>
              </li>
            )}

            {isNonEmpty(project.wohneinheiten) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Wohneinheiten / Zimmer</div>
                <div className={styles.cardValue}>{project.wohneinheiten}</div>
              </li>
            )}

            {isNonEmpty(gewerbeeinheiten) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Gewerbeeinheiten</div>
                <div className={styles.cardValue}>{gewerbeeinheiten}</div>
              </li>
            )}

            {isNonEmpty(anzahlStellplaetze) && (
              <li className={styles.cardItem}>
                <div className={styles.cardLabel}>Anzahl Stellplätze</div>
                <div className={styles.cardValue}>{anzahlStellplaetze}</div>
              </li>
            )}

            {isNonEmpty(besonderheit) &&
              (Array.isArray(besonderheit) ? (
                <li className={styles.cardItem}>
                  <div className={styles.cardLabel}>Besonderheit</div>
                  <ul className={styles.cardValueList}>
                    {besonderheit.map((item: string, index: number) => (
                      <li key={index} className={styles.cardValueItem}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li className={styles.cardItem}>
                  <div className={styles.cardLabel}>Besonderheit</div>
                  <div className={styles.cardValue}>{besonderheit}</div>
                </li>
              ))}
          </ul>

          <div className={styles.backButton}>
            <CustomButton
              text="← zu den Projekten"
              to="/projects"
              variant="filled"
              size="medium"
            />
          </div>
        </div>
      </section>
    </div>
  );

   return (
    <main className={styles.projectDetails}>
      <ScrollStack
        images={project.images}
        onScrollEnd={() => setShowDescription(true)}
        onScrollHide={() => setShowDescription(false)}
        credits={project.credits}
      >
        {descriptionMarkup}
      </ScrollStack>

      <section className={styles.footerSpacer} />
      <BackToTop />
    </main>
  );
};

export default ProjectDetails;